import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RoleService, AdminService, ApiService } from "../../api";
import { Flags, Permissions, translate } from "../../common/providers";
import { exportView } from "../../navigation";
import AdminUsers from "./panels/admin-users/AdminUsers";
import styles from "./Admin.module.css";
import { setIsEditBu } from "../../common/slice";

const Admin = () => {
	const [companies, setCompanies] = useState([]);
	const [companyRoles, setCompanyRoles] = useState([]);
	const [companyIdToUser, setCompanyIdToUser] = useState(new Map());
	const [companyIdToProject, setCompanyIdToProject] = useState(new Map());
	const cancelTokenSourceRef = useRef(null);
	const isEditBu = useSelector(({ context }) => context.isEditBu);
	const dispatch = useDispatch();
	useEffect(() => {
		const documentTitle = translate("admin.document.title");
		document.title = documentTitle;
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);
	useEffect(() => {
		if (!Array.isArray(companies) || companies.length === 0 || isEditBu) {
			AdminService.getCompanyTree(cancelTokenSourceRef.current.token)
				.then((dataTree) => {
					setCompanies(dataTree);
				})
				.catch((err) => {
					console.error(err);
				})
				.finally(() => {
					dispatch(setIsEditBu(false));
				});
		}
	}, [companies, isEditBu, dispatch]);
	useEffect(() => {
		if (!Array.isArray(companyRoles) || companyRoles.length === 0) {
			RoleService.listUserInCompany(cancelTokenSourceRef.current.token)
				.then((data) => {
					if (Array.isArray(data)) {
						const formattedData = data.map((d) => {
							const formatted = d?.toLowerCase().split("_").join(" ");
							return {
								name: d,
								displayName:
									formatted.substring(0, 1).toUpperCase() + formatted.substring(1, formatted.length),
							};
						});
						setCompanyRoles(formattedData);
					}
				})
				.catch((err) => console.error(err));
		}
	}, [companyRoles]);

	const getProjects = ({ company }) => {
		const companyIdToProjectMap = new Map();
		if (!Array.isArray(company.projects) || company.projects.length === 0) {
			AdminService.getCompanyProjects({ companyId: company.id }, cancelTokenSourceRef.current.token)
				.then((data) => {
					if (Array.isArray(data)) {
						companyIdToProjectMap.set(company.id, data);
						setCompanyIdToProject(() =>
							companyIdToProjectMap.get(companyIdToProjectMap.keys().next().value)
						);
					}
				})
				.catch((err) => console.error(err));
		}
	};
	const getUsers = ({ company }) => {
		const companyIdToUserMap = new Map();
		if (!Array.isArray(company?.users) || company?.users.length === 0) {
			AdminService.getCompanyUsers({ companyId: company.id }, cancelTokenSourceRef.current.token)
				.then((data) => {
					companyIdToUserMap.set(company.id, data);
					setCompanyIdToUser(() => companyIdToUserMap.get(companyIdToUserMap.keys().next().value));
				})
				.catch((err) => console.error(err));
		}
	};
	const changeRole = ({ companyId, userId, role }) => {
		if (!companyId || !userId || !role) {
			return;
		}
		AdminService.updateRoleCompany({ companyId, userId }, { role }, cancelTokenSourceRef.current.token)
			.then(() => {
				const newUsers = [...companyIdToUser];
				const indexUser = newUsers.findIndex((u) => u.id === userId);
				newUsers[indexUser].companyRole = role;
				setCompanyIdToUser(newUsers);
			})
			.catch((err) => {
				console.error(err);
			});
	};
	const changeLockCompany = ({ companyId, userId, isLocked = false }) => {
		if (!companyId || !userId) {
			return;
		}
		if (isLocked) {
			AdminService.unlockUser({ companyId, userId }, cancelTokenSourceRef.current.token)
				.then(() => {
					const newUsers = [...companyIdToUser];
					const indexUser = newUsers.findIndex((u) => u.id === userId);
					newUsers[indexUser].locked = false;
					setCompanyIdToUser(newUsers);
				})
				.catch((err) => console.error(err));
		} else {
			AdminService.lockUser({ companyId, userId }, "LOCKED", cancelTokenSourceRef.current.token)
				.then(() => {
					const newUsers = [...companyIdToUser];
					const indexUser = newUsers.findIndex((u) => u.id === userId);
					newUsers[indexUser].locked = true;
					setCompanyIdToUser(newUsers);
				})
				.catch((err) => console.error(err));
		}
	};
	const addUser = ({ companyId, payload }) => {
		if (!companyId || !payload) {
			return;
		}
		AdminService.createCompanyUser(payload, { companyId }, cancelTokenSourceRef.current.token)
			.then((data) => {
				const companyIdToUserNew = [...companyIdToUser, data];
				setCompanyIdToUser(companyIdToUserNew);
			})
			.catch((err) => console.error(err));
	};
	const renameProject = ({ companyId, projectId, name, status }) => {
		if (!companyId || !projectId) {
			return;
		}
		AdminService.updateProjectName(
			{ companyId, projectId },
			{ name, currentStatus: status },
			cancelTokenSourceRef.current.token
		)
			.then(() => {
				const newProject = [...companyIdToProject];
				const indexProject = newProject.findIndex((u) => u.id === projectId);
				newProject[indexProject].name = name;
				setCompanyIdToProject(newProject);
			})
			.catch((err) => console.error(err));
	};
	const archiveProject = ({ companyId, projectId, name, status }) => {
		if (!companyId || !projectId) {
			return;
		}
		AdminService.updateProjectStatus(
			{ companyId, projectId },
			{ name, currentStatus: status },
			cancelTokenSourceRef.current.token
		)
			.then(() => {
				const newProject = [...companyIdToProject];
				const indexProject = newProject.findIndex((u) => u.id === projectId);
				newProject[indexProject].currentStatus = status;
				setCompanyIdToProject(newProject);
			})
			.catch((err) => console.error(err));
	};
	return (
		<div className={styles.container}>
			{Array.isArray(companies) && companies.length > 0 ? (
				<AdminUsers
					companies={companies}
					COMPANY_ROLES={companyRoles}
					companyIdToProject={companyIdToProject}
					companyIdToUser={companyIdToUser}
					getCompanyProjects={getProjects}
					getCompanyUsers={getUsers}
					onAddUser={addUser}
					onChangeArchive={archiveProject}
					onChangeLock={changeLockCompany}
					onChangeName={renameProject}
					onChangeRole={changeRole}
				/>
			) : (
				<div className={styles.emptyState}>
					<span className={styles.emptyState__text}>
						{translate("admin.admin.empty-state.no-company-registered")}
					</span>
				</div>
			)}
		</div>
	);
};

export default exportView({
	path: "/admin",
	localesPath: "/admin/locales",
	component: Admin,
	flag: Flags.ADMIN,
	role: Permissions.COMPANY_ADMIN,
});
